import React, { useState, useEffect } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { FaBars, FaTimes, FaInstagram, FaFacebook, FaWhatsapp, FaLinkedinIn, FaUserCircle, FaSignOutAlt, FaShieldAlt } from 'react-icons/fa'
import { useAuth } from '../../context/AuthContext'
import NotificationBellPopup from '../common/NotificationBellPopup'

// Glowing social icon with animated pulse ring
const GlowSocialIcon = ({ icon: Icon, href, label, bg, glow, ring }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    style={{ '--glow-color': glow }}
    className="relative group flex items-center justify-center"
  >
    {/* Animated pulse ring */}
    <span
      className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-opacity duration-300"
      style={{ backgroundColor: ring }}
    />
    {/* Icon button */}
    <span
      className="relative z-10 w-9 h-9 rounded-full flex items-center justify-center text-white transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg"
      style={{ background: bg, boxShadow: `0 0 0 0 ${ring}` }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = `0 0 14px 4px ${ring}`}
      onMouseLeave={e => e.currentTarget.style.boxShadow = `0 0 0 0 ${ring}`}
    >
      <Icon size={16} />
    </span>
  </a>
)

const Header = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [showDiwaliBar, setShowDiwaliBar] = useState(() => {
    return sessionStorage.getItem('hideDiwaliBar') !== 'true'
  })
  const { user, isAuthenticated, logout } = useAuth()

  const dismissDiwaliBar = (e) => {
    e.stopPropagation()
    setShowDiwaliBar(false)
    sessionStorage.setItem('hideDiwaliBar', 'true')
  }

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/properties', label: 'Properties' },
    { path: '/about', label: 'About Us' },
    { path: '/blogs', label: 'Blogs' },
    { path: '/contact', label: 'Contact' },
  ]

  const socials = [
    {
      icon: FaInstagram,
      href: 'https://www.instagram.com/chapter24_official',
      label: 'Instagram',
      bg: 'linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)',
      glow: '#fd1d1d',
      ring: 'rgba(253,29,29,0.35)',
    },
    {
      icon: FaFacebook,
      href: 'https://www.facebook.com/people/Chapter24/61587948214392/',
      label: 'Facebook',
      bg: '#1877F2',
      glow: '#1877F2',
      ring: 'rgba(24,119,242,0.35)',
    },
    {
      icon: FaWhatsapp,
      href: 'https://wa.me/919960697118',
      label: 'WhatsApp',
      bg: '#25D366',
      glow: '#25D366',
      ring: 'rgba(37,211,102,0.35)',
    },
    {
      icon: FaLinkedinIn,
      href: 'https://www.linkedin.com/company/chapter24-official/',
      label: 'LinkedIn',
      bg: '#0077B5',
      glow: '#0077B5',
      ring: 'rgba(0,119,181,0.35)',
    },
  ]

    return (
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${scrolled
          ? 'bg-gradient-to-r from-pink-100/95 via-pink-50/90 to-purple-100/95 backdrop-blur-md shadow-glow'
          : 'bg-gradient-to-r from-pink-100/90 via-pink-50/85 to-purple-100/90 backdrop-blur-md shadow-xs'
          }`}
      >
        {/* Diwali Dhamaka 2026 Announcement Bar */}
        {showDiwaliBar && (
          <div className="bg-gradient-to-r from-amber-600 via-rose-600 to-purple-800 text-white text-[11px] sm:text-xs font-bold py-2 px-3 shadow-inner relative flex items-center justify-between gap-2 border-b border-amber-300/30">
            <div className="container-custom mx-auto flex items-center justify-center gap-2 sm:gap-4 flex-wrap text-center pr-6 sm:pr-0">
              <span className="inline-flex items-center gap-1.5 text-yellow-200 tracking-wide">
                <span>🪔</span>
                <span className="font-extrabold uppercase">Diwali Dhamaka 2026:</span>
                <span className="text-white font-black bg-rose-700/80 px-2 py-0.5 rounded-md">50% OFF 1st Month Rent</span>
              </span>

              <span className="hidden md:inline text-amber-200 font-medium">
                📅 Booking: 1st–9th Nov 2026
              </span>

              <span className="hidden lg:inline text-gray-200">
                Pimple Saudagar • Tathawade • Nigdi • Kiwale • Pradhikaran
              </span>

              <a
                href="https://wa.me/919960697118?text=Hi%20Chapter24!%20🪔%20I%20want%20to%20claim%20the%20Diwali%20Dhamaka%2050%25%20OFF%20offer%20on%20first%20month's%20rent!"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 bg-yellow-400 hover:bg-yellow-300 text-purple-950 font-black px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs transition shadow-sm uppercase tracking-wide"
              >
                <FaWhatsapp className="text-xs text-green-700" /> Book via WhatsApp
              </a>
            </div>

            <button
              onClick={dismissDiwaliBar}
              aria-label="Dismiss Diwali announcement"
              className="text-white/70 hover:text-white p-1 rounded-full transition shrink-0"
            >
              <FaTimes size={12} />
            </button>
          </div>
        )}

        <nav className="container-custom">
          <div className="flex justify-between items-center py-3">

          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 gap-2 sm:gap-3 transition-transform duration-300 hover:scale-105">
            {/* Logo Graphic */}
            <img
              src="/logo.png"
              alt="Chapter 24 Logo"
              className="h-12 sm:h-14 w-auto object-contain"
            />

            {/* Logo Text */}
            <div className="flex flex-col hidden sm:flex">
              <div className="flex items-baseline">
                <span className="font-heading font-extrabold text-xl sm:text-2xl text-primary-700">Chapter</span>
                <span className="font-heading font-extrabold text-xl sm:text-2xl text-accent-500 ml-0.5">24</span>
              </div>
              <div className="flex items-baseline text-xs font-semibold tracking-wide">
                <span className="text-accent-500">Working</span>
                <span className="text-primary-700 ml-1">Women's Residence</span>
              </div>
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center space-x-6">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={({ isActive }) =>
                  `relative transition-colors font-semibold text-xs tracking-wide after:content-[''] after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:rounded-full after:bg-gradient-to-r after:from-pink-500 after:to-purple-600 after:transition-all after:duration-300 ${isActive
                    ? 'text-primary-800 font-extrabold after:w-full'
                    : 'text-secondary-800 hover:text-primary-700 after:w-0 hover:after:w-full'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          {/* Desktop: CTA + Auth + Social Icons */}
          <div className="hidden md:flex items-center gap-2.5">
            {/* Notification Bell Popup (Only shown for authenticated users) */}
            {isAuthenticated && user && (
              <NotificationBellPopup targetRole={user?.role === 'admin' ? 'ADMIN' : 'RESIDENT'} align="right" />
            )}

            {/* User Auth state */}
            {isAuthenticated ? (
              <div className="flex items-center gap-2 bg-purple-50/80 hover:bg-purple-100/80 transition-colors px-3 py-1.5 rounded-2xl border border-purple-200 shadow-2xs">
                {user?.role === 'admin' ? (
                  <Link
                    to="/admin"
                    className="flex items-center gap-1.5 text-xs font-bold text-primary-800 hover:text-primary-600"
                    title="Open Admin Console"
                  >
                    <FaShieldAlt className="text-primary-600" size={13} />
                    <span>Admin Console</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-primary-200 text-primary-900 font-extrabold rounded-md uppercase">
                      Admin
                    </span>
                  </Link>
                ) : (
                  <Link
                    to="/portal"
                    className="flex items-center gap-1.5 text-xs font-bold text-primary-800 hover:text-primary-600"
                    title="Open Resident Portal"
                  >
                    <FaUserCircle className="text-primary-600" size={13} />
                    <span>{user?.fullName?.split(' ')[0] || 'Resident'}</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-800 font-extrabold rounded-md uppercase">
                      Portal
                    </span>
                  </Link>
                )}
                <button
                  onClick={logout}
                  title="Sign Out"
                  className="text-gray-400 hover:text-red-600 pl-1.5 ml-1 border-l border-purple-200 transition-colors cursor-pointer"
                >
                  <FaSignOutAlt size={12} />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="text-xs font-bold text-primary-700 hover:text-primary-900 px-3.5 py-2 rounded-xl border border-primary-200 hover:bg-primary-50 transition-all flex items-center gap-1.5 shadow-2xs"
              >
                <FaUserCircle size={14} />
                <span>Login</span>
              </Link>
            )}

            {/* Apply Now CTA */}
            <Link to="/apply" className="btn-primary text-xs px-4 py-2 font-bold shadow-sm whitespace-nowrap">
              Book your Space
            </Link>

            {/* Divider */}
            <div className="h-6 w-px bg-gray-200 mx-0.5" />

            {/* Glowing Social Icons */}
            {socials.map(s => (
              <GlowSocialIcon key={s.label} {...s} />
            ))}
          </div>

          {/* Mobile: Social Icons & Notification Bell */}
          <div className="flex md:hidden items-center gap-2">
            {isAuthenticated && user && (
              <NotificationBellPopup align="right" targetRole={user?.role === 'admin' ? 'ADMIN' : 'RESIDENT'} />
            )}
            {socials.map(s => (
              <GlowSocialIcon key={s.label} {...s} />
            ))}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-2 rounded-lg text-secondary-700 hover:text-primary-600 hover:bg-primary-50 transition-all"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-purple-200/50 animate-fade-in bg-gradient-to-b from-pink-50/95 via-white to-purple-50/95">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  `flex items-center py-2.5 px-4 rounded-xl text-secondary-800 hover:text-primary-700 hover:bg-pink-100/60 transition-all font-semibold text-sm ${isActive ? 'text-primary-800 font-bold bg-pink-100/70 border-l-4 border-primary-600' : ''
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}

            {isAuthenticated ? (
              <div className="mx-4 mt-3 p-3 bg-purple-50 rounded-xl border border-purple-200 flex items-center justify-between">
                <Link
                  to={user?.role === 'admin' ? '/admin' : '/portal'}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  {user?.role === 'admin' ? (
                    <FaShieldAlt className="text-primary-600" size={18} />
                  ) : (
                    <FaUserCircle className="text-primary-600" size={20} />
                  )}
                  <div>
                    <div className="text-xs font-bold text-gray-900">
                      {user?.role === 'admin' ? 'Admin Console' : (user?.fullName || user?.username)}
                    </div>
                    <div className="text-[10px] text-primary-700 font-bold uppercase">
                      {user?.role} portal &rarr;
                    </div>
                  </div>
                </Link>
                <button
                  onClick={() => { logout(); setIsOpen(false); }}
                  className="text-xs font-bold text-red-600 bg-white px-2.5 py-1 rounded-lg border border-red-200 hover:bg-red-50 cursor-pointer"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-center gap-2 mx-4 mt-3 py-2.5 rounded-xl text-primary-700 bg-purple-50 font-bold text-xs border border-purple-200"
              >
                <FaUserCircle size={14} />
                <span>Login to Resident / Admin Portal</span>
              </Link>
            )}

            <Link
              to="/apply"
              onClick={() => setIsOpen(false)}
              className="block mt-3 mx-4 btn-primary text-center text-xs py-2.5 font-bold"
            >
              Book Your Space
            </Link>
          </div>
        )}
      </nav>

      {/* Pink to Purple Gradient Drag Accent Border */}
      <div className="h-[2.5px] w-full bg-gradient-to-r from-pink-500 via-primary-500 to-purple-700 opacity-90 shadow-2xs" />
    </header>
  )
}

export default Header